/** @format */
"use client";
import Loader from "./Loader";
import React, { useEffect, useState, useCallback } from "react";
import MaterialBankBanner from "./components/MaterialBankBanner";
import FilterSection from "./components/FilterSection";
import NoMatchingFabrics from "./components/NoMatchingFabrics";
import FabricList from "./components/FabricList";
import { EXCLUDED_SERIES, DISCONTINUED_SKU } from "./constants/fabricData";
import { capitalizeFirstLetters } from "./utils/stringUtils";
import { INITIAL_FILTERS } from "./utils/fabricUtils";

export default function Sample() {
	const [filters, setFilters] = useState(INITIAL_FILTERS);
	const [data, setData] = useState([]);
	const [filteredFabrics, setFilteredFabrics] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [groupList, setGroupList] = useState(["All"]);
	const [colourList, setColourList] = useState(["All"]);

	const fetchFabricData = useCallback(async () => {
		try {
			setIsLoading(true);
			const res = await fetch(
				"https://dealerportal.mysunglow.com/api/addons/getFreeSample"
			);
			const result = await res.json();
			setData(result);
		} catch (e) {
			console.error("Error fetching data:", e);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const filterFabrics = useCallback(() => {
		if (!data.length) return;

		setIsLoading(true);
		try {
			const filtered = data
				.filter((item) =>
					item.shade_type.toLowerCase().includes(filters.shade.toLowerCase())
				)
				.sort((a, b) => a.Series.localeCompare(b.Series));

			const groups = Array.from(
				new Set(filtered?.map((item) => capitalizeFirstLetters(item?.Group)))
			).sort();

			const colours = Array.from(
				new Set(
					filtered?.map((item) => capitalizeFirstLetters(item?.Colour_Family))
				)
			).sort();

			const filteredFabrics = filtered.filter((item) => {
				const hasGreenRating = item.Green_Rating !== null;
				const hasFireRating = item.Fire_Rating !== null;
				const hasAntimicrobial = item.Antimicrobial_Rating !== null;

				return (
					(filters.group === "All" ||
						capitalizeFirstLetters(item?.Group) === filters.group) &&
					(filters.color === "All" ||
						capitalizeFirstLetters(item?.Colour_Family) === filters.color) &&
					(!filters["eco-friendly"] || hasGreenRating) &&
					(!filters["fire-resistant"] || hasFireRating) &&
					(!filters["anti-microbial"] || hasAntimicrobial) &&
					!DISCONTINUED_SKU.includes(item.Code)
				);
			});

			const fabricsToShow = filteredFabrics.reduce((acc, fabric) => {
				if (!EXCLUDED_SERIES.includes(fabric.Series.toLowerCase())) {
					if (!acc[fabric.Series]) {
						acc[fabric.Series] = {
							fabrics: [],
							group: fabric.Group,
							colour: fabric.Colour_Family,
							composition: fabric.Composition,
							fireRating: fabric.Fire_Rating,
							antimicro: fabric.Antimicrobial_Rating,
							greenRating: fabric.Green_Rating,
						};
					}
					acc[fabric.Series].fabrics.push(fabric);
				}
				return acc;
			}, {});

			setFilteredFabrics(fabricsToShow);
			setGroupList(["All", ...groups]);
			setColourList(["All", ...colours]);
		} finally {
			setIsLoading(false);
		}
	}, [data, filters]);

	useEffect(() => {
		fetchFabricData();
	}, [fetchFabricData]);

	useEffect(() => {
		filterFabrics();
	}, [filters, filterFabrics]);

	const filterProps = {
		shade: {
			selected: filters.shade,
			setSelected: (shade) =>
				setFilters((prev) => ({
					...prev,
					shade,
					group: "All",
					color: "All",
					"eco-friendly": false,
					"fire-resistant": false,
					"anti-microbial": false,
				})),
		},
		group: {
			selected: filters.group,
			setSelected: (group) =>
				setFilters((prev) => ({ ...prev, group, color: "All" })),
			list: groupList,
		},
		color: {
			selected: filters.color,
			setSelected: (color) => setFilters((prev) => ({ ...prev, color })),
			list: colourList,
		},
		properties: {
			"eco-friendly": {
				checked: filters["eco-friendly"],
				setChecked: (checked) =>
					setFilters((prev) => ({ ...prev, "eco-friendly": checked })),
			},
			"fire-resistant": {
				checked: filters["fire-resistant"],
				setChecked: (checked) =>
					setFilters((prev) => ({ ...prev, "fire-resistant": checked })),
			},
			"anti-microbial": {
				checked: filters["anti-microbial"],
				setChecked: (checked) =>
					setFilters((prev) => ({ ...prev, "anti-microbial": checked })),
			},
		},
	};

	return (
		<div className="w-full">
			<FilterSection {...filterProps} />
			<MaterialBankBanner />
			{isLoading ? (
				<Loader />
			) : Object.keys(filteredFabrics).length === 0 ? (
				<NoMatchingFabrics />
			) : (
				<FabricList
					filteredFabrics={filteredFabrics}
					selectedShade={filters.shade}
				/>
			)}
		</div>
	);
}
